import { CloseOutlined } from "@ant-design/icons"
import { Button, Flex, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { GLOBAL_MAP_SURFACE_CLASS } from "../global-map-surface.ts"
import { EntityGlyphTile } from "../map/EntityGlyphTile.tsx"
import { EntityIcon } from "../map/EntityIcon.tsx"
import type { MapFeature } from "../redux/global-map.types"

const { Title, Text } = Typography

interface DerivedEntityCardProps {
  feature: MapFeature
  onClose: () => void
  // Opens the Lead/Project this marker is drawn on — the record is what has
  // a page; a subcontractor assignment and an appointment do not.
  onOpenRecord: () => void
  canOpenRecord: boolean
}

/**
 * The details panel for a Subcontractor or Appointment marker.
 *
 * Those two layers are drawn on the Lead/Project records they hang off (see
 * `aggregation.build_subcontractor_features`), and neither has a quick-view
 * endpoint of its own — so this card renders exactly what the marker row
 * already carries (name, trade or time, the record it belongs to) and hands
 * the user on to that record, rather than inventing fields the API does not
 * answer for.
 */
export function DerivedEntityCard({
  feature,
  onClose,
  onOpenRecord,
  canOpenRecord
}: DerivedEntityCardProps) {
  const { t } = useTranslation()
  const isAppointment = feature.entity_type === "appointment"
  // An appointment names its lead/project, shown the way that record's own
  // marker reads on hover; older payloads without it keep the plain rows.
  const recordType = isAppointment ? feature.record_type : null

  return (
    <div
      className={`flex h-full flex-col ${GLOBAL_MAP_SURFACE_CLASS}`}
      data-testid="global-map-derived-card"
    >
      <Flex align="start" justify="space-between" gap={8} className="p-4 pb-3">
        <Flex gap={12} align="center" className="min-w-0">
          <EntityGlyphTile entityType={feature.entity_type} size={44} />
          <Flex vertical className="min-w-0">
            <Title
              level={5}
              className="!mb-0 min-w-0 truncate !text-[18px] font-semibold !text-[#0F172A]"
            >
              {feature.title}
            </Title>
            <Text className="truncate text-[13px] text-slate-500">
              {t(`globalMap.entityLabel.${feature.entity_type}`)}
            </Text>
          </Flex>
        </Flex>
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          aria-label={t("globalMap.quickView.actions.close")}
          onClick={onClose}
        />
      </Flex>

      <dl className="m-0 flex flex-col gap-3 border-t border-gray-100 px-4 py-4">
        {feature.status_label && (
          <div className="flex flex-col gap-1">
            <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
              {t(
                isAppointment
                  ? "globalMap.derived.whenLabel"
                  : "globalMap.derived.tradeLabel"
              )}
            </dt>
            <dd className="m-0 text-[14px] font-medium text-slate-800">
              {feature.status_label}
            </dd>
          </div>
        )}
        {recordType && (
          <div className="flex flex-col gap-1">
            <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
              {t(`globalMap.entityLabel.${recordType}`)}
            </dt>
            <dd className="m-0">
              <button
                type="button"
                onClick={onOpenRecord}
                disabled={!canOpenRecord}
                data-testid="global-map-derived-record"
                className="flex w-full items-start gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-xs shadow-sm transition-colors enabled:cursor-pointer enabled:hover:border-primary/40 enabled:hover:bg-primary-bg"
              >
                <EntityIcon
                  entityType={recordType}
                  className="size-6 shrink-0"
                />
                <span className="flex min-w-0 flex-col gap-0.5">
                  {feature.record_reference && (
                    <span className="truncate text-[11px] text-gray-500">
                      {feature.record_reference}
                    </span>
                  )}
                  <span className="truncate text-[13px] font-semibold text-gray-900">
                    {feature.record_title || feature.reference}
                  </span>
                  <span className="truncate text-gray-500">
                    {feature.customer_name ?? "—"}
                  </span>
                  <span className="truncate text-gray-500">
                    {feature.address ?? "—"}
                  </span>
                </span>
              </button>
            </dd>
          </div>
        )}
        {!recordType && feature.reference && (
          <div className="flex flex-col gap-1">
            <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
              {t("globalMap.derived.recordLabel")}
            </dt>
            <dd className="m-0 truncate text-[14px] font-medium text-slate-800">
              {feature.reference}
            </dd>
          </div>
        )}
        {!recordType && feature.address && (
          <div className="flex flex-col gap-1">
            <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
              {t("globalMap.quickView.fields.address")}
            </dt>
            <dd className="m-0 text-[14px] text-slate-700">
              {feature.address}
            </dd>
          </div>
        )}
      </dl>

      {canOpenRecord && (
        <div className="mt-auto border-t border-gray-100 p-4">
          <Button
            type="primary"
            block
            onClick={onOpenRecord}
            className="h-10 rounded-lg text-[13px] font-medium"
          >
            {t(
              recordType === "lead"
                ? "globalMap.derived.openLead"
                : recordType === "project"
                  ? "globalMap.derived.openProject"
                  : "globalMap.derived.openRecord"
            )}
          </Button>
        </div>
      )}
    </div>
  )
}

export default DerivedEntityCard
